import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FaPlus, FaTimes, FaTrash, FaExclamationTriangle, FaPaperclip } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import DOMPurify from 'dompurify';
import Button from '../../util_components/button';
import CustomInput from '../../util_components/custom_input';
import CustomLoading from '../../util_components/custom_loading';
import SearchableSelect from '../reminders/SearchableSelect';
import { get_api_url } from '../../../utils/urls';
import { get_api, post_api, api_with_method } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';
import { format_field_value } from './format_value';
import 'react-quill/dist/quill.snow.css';
import './toggle.css';

// Same rich-text toolbar as the student-profile notes composer.
const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'clean']
  ]
};
const QUILL_FORMATS = ['bold', 'italic', 'list', 'bullet', 'link'];

const YES_NO = [
  { value: 'Yes', label: 'Yes', color: '#22c55e' },
  { value: 'No', label: 'No', color: '#ef4444' }
];
const YES_NO_NA = [
  { value: 'Yes', label: 'Yes', color: '#22c55e' },
  { value: 'No', label: 'No', color: '#ef4444' },
  { value: 'Not Applicable', label: 'N/A', color: '#9ca3af' }
];
const STATUS_OPTS = [
  { value: 'Red', label: 'Red', color: '#ef4444' },
  { value: 'Yellow', label: 'Yellow', color: '#f59e0b' },
  { value: 'Green', label: 'Green', color: '#22c55e' }
];
const STRENGTH_OPTS = [
  { value: 'Strong', label: 'Strong', color: '#22c55e' },
  { value: 'Weak', label: 'Weak', color: '#ef4444' }
];

// Related field pairs rendered together in one row (each label/value on its own
// aligned line) instead of two separate rows.
const COMBINE_GROUPS = [
  ['five_star_reviews', 'one_two_star_reviews'],
  ['child_signups', 'child_members'],
  ['family_plan_invites', 'family_plan_members']
];

// Small round icon button. Explicit background/border overrides the host CMS's
// global orange `button {}` rule (which otherwise renders these as orange blobs).
const ICON_BTN_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 28,
  width: 28,
  borderRadius: 9999,
  border: '1px solid #d1d5db',
  background: 'var(--surface-card)',
  cursor: 'pointer',
  padding: 0
};

// Per-checkpoint checklist form. The field list, which fields are read-only
// (blue/auto), and which are hidden (red/not-applicable) all come from the
// server schema — this component just renders widgets by shape.
class ChecklistForm extends Component {
  state = {
    loading: true,
    saving: false,
    meta: {},
    fields: [],
    reply: {},
    auto_data: {},
    teacher_options: [],
    group_class_options: [],
    uploading: false
  };

  scroll_ref = React.createRef();

  file_input_ref = React.createRef();

  componentDidMount() {
    this.load_form();
  }

  // ── unanswered tracking ───────────────────────────────────────────────────
  // Toggle-shaped fields that demand a pick. Composite shapes store the pick
  // under `.value`; the rest store it directly.
  is_toggle_unanswered = (field) => {
    if (field.read_only) return false;
    const raw = this.state.reply[field.key];
    switch (field.shape) {
      case 'yes_no':
      case 'yes_no_na':
      case 'status':
        return !raw;
      case 'yes_no_teacher':
      case 'group_classes_found':
        return !(raw && raw.value);
      default:
        return false;
    }
  };

  count_unanswered = () => this.state.fields.filter(this.is_toggle_unanswered).length;

  // Scroll the first still-empty toggle into view when the banner is clicked.
  scroll_to_first_unanswered = () => {
    const container = this.scroll_ref.current;
    if (!container) return;
    const target = container.querySelector('.erc-toggle--empty');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  load_form = () => {
    this.setState({ loading: true });
    const url = get_api_url(`/v2/early_retention_checklist/form/${this.props.uuid}`);
    get_api(url, true)
      .then((res) => {
        const d = res.data.data;
        this.setState({
          loading: false,
          fields: d.fields || [],
          reply: d.reply || {},
          auto_data: d.auto_data || {},
          teacher_options: d.teachers || [],
          group_class_options: d.group_classes || [],
          meta: {
            student_name: d.student_name,
            student_uuid: d.student_uuid,
            check_label: d.check_label,
            checkin_date: d.checkin_date,
            day_number: d.day_number,
            week_number: d.week_number,
            pause_days_added: d.pause_days_added,
            status: d.status,
            submitted_by_name: d.submitted_by_name
          }
        });
      })
      .catch((e) => {
        let err = 'Failed to load form';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
        this.setState({ loading: false });
      });
  };

  // ── value setters ───────────────────────────────────────────────────────
  set_value = (key, value) => this.setState((prev) => ({ reply: { ...prev.reply, [key]: value } }));

  set_obj_value = (key, subkey, value) =>
    this.setState((prev) => ({
      reply: { ...prev.reply, [key]: { ...(prev.reply[key] || {}), [subkey]: value } }
    }));

  // ── teacher picker list (Main Teacher Found) ──────────────────────────────
  // Value shape: { value: 'Yes'|'No', teachers: [{ uuid, name }] }
  teacher_rows = (key) => {
    const obj = this.state.reply[key] || {};
    const list = Array.isArray(obj.teachers) ? obj.teachers : [];
    return list.length ? list : [{ uuid: '', name: '' }];
  };

  set_teacher = (key, idx, uuid) => {
    const opt = this.state.teacher_options.find((o) => o.value === uuid);
    const rows = this.teacher_rows(key).map((t, i) =>
      i === idx ? { uuid, name: opt ? opt.label : '' } : t
    );
    this.set_obj_value(key, 'teachers', rows);
  };

  add_teacher = (key) =>
    this.set_obj_value(key, 'teachers', [...this.teacher_rows(key), { uuid: '', name: '' }]);

  remove_teacher = (key, idx) => {
    const rows = this.teacher_rows(key).filter((_, i) => i !== idx);
    this.set_obj_value(key, 'teachers', rows);
  };

  // ── group classes found ───────────────────────────────────────────────────
  // Value shape: { value: 'Yes'|'No'|'Not Applicable',
  //                classes: [{ value, name, teacher_name, teacher_uuid, day_of_week, time }] }
  gc_rows = (key) => {
    const obj = this.state.reply[key] || {};
    const list = Array.isArray(obj.classes) ? obj.classes : [];
    return list.length ? list : [{ value: '' }];
  };

  set_gc = (key, idx, optionValue) => {
    const opt = this.state.group_class_options.find((o) => o.value === optionValue);
    const cls = opt
      ? {
          value: opt.value,
          name: opt.label,
          teacher_name: opt.teacher_name,
          teacher_uuid: opt.teacher_uuid,
          day_of_week: opt.day_of_week,
          time: opt.time
        }
      : { value: '' };
    const list = this.gc_rows(key).map((c, i) => (i === idx ? cls : c));
    this.set_obj_value(key, 'classes', list);
  };

  add_gc = (key) => this.set_obj_value(key, 'classes', [...this.gc_rows(key), { value: '' }]);

  remove_gc = (key, idx) =>
    this.set_obj_value(
      key,
      'classes',
      this.gc_rows(key).filter((_, i) => i !== idx)
    );

  // ── teacher relationship list ─────────────────────────────────────────────
  rel_list = (key) => (Array.isArray(this.state.reply[key]) ? this.state.reply[key] : []);

  add_rel = (key) =>
    this.set_value(key, [...this.rel_list(key), { teacher_name: '', strength: '' }]);

  update_rel = (key, idx, subkey, value) => {
    const list = this.rel_list(key).map((r, i) => (i === idx ? { ...r, [subkey]: value } : r));
    this.set_value(key, list);
  };

  // The relationship picker only offers the teachers chosen in "Main Teacher
  // Found" — {value,label} from that field's selected teachers.
  main_teacher_options = () => {
    const mt = this.state.reply.main_teacher_found || {};
    const list = Array.isArray(mt.teachers) ? mt.teachers : [];
    return list.filter((t) => t.uuid).map((t) => ({ value: t.uuid, label: t.name || t.uuid }));
  };

  // Set the teacher on a relationship row from the type-to-filter selector,
  // storing both the uuid and resolved display name.
  set_rel_teacher = (key, idx, uuid) => {
    const opt = this.main_teacher_options().find((o) => o.value === uuid);
    const list = this.rel_list(key).map((r, i) =>
      i === idx ? { ...r, uuid, teacher_name: opt ? opt.label : '' } : r
    );
    this.set_value(key, list);
  };

  remove_rel = (key, idx) =>
    this.set_value(
      key,
      this.rel_list(key).filter((_, i) => i !== idx)
    );

  // ── attachments (stored in reply.attachments; persisted on submit) ─────────
  attachments = () =>
    Array.isArray(this.state.reply.attachments) ? this.state.reply.attachments : [];

  upload_attachment = (file) => {
    this.setState({ uploading: true });
    const url = get_api_url('/v2/early_retention_checklist/upload_attachment');
    const form = new FormData();
    form.append('student_uuid', this.state.meta.student_uuid);
    form.append('folder', 'early_retention');
    form.append('file', file);
    api_with_method('post', url, form, true, 'formdata')
      .then((res) => {
        const att = res?.data?.data;
        if (att && att.key) this.set_value('attachments', [...this.attachments(), att]);
      })
      .catch((e) => {
        const msg = e?.response?.data?.reason || 'Failed to upload attachment';
        this.props.set_notification_variable(true, 'error', msg);
      })
      .finally(() => this.setState({ uploading: false }));
  };

  remove_attachment = (idx) =>
    this.set_value(
      'attachments',
      this.attachments().filter((_, i) => i !== idx)
    );

  handle_attachment_change = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) this.upload_attachment(file);
    e.target.value = '';
  };

  // ── persistence ───────────────────────────────────────────────────────────
  save_draft = () => this.persist('/v2/early_retention_checklist/save_draft', false);

  submit = () => this.persist('/v2/early_retention_checklist/submit', true);

  persist = (path, is_submit) => {
    this.setState({ saving: true });
    const url = get_api_url(path);
    post_api(url, { uuid: this.props.uuid, reply: this.state.reply }, true)
      .then(() => {
        this.setState({ saving: false });
        if (is_submit) {
          this.props.set_notification_variable(true, 'success', 'Checklist submitted');
          this.props.on_submitted();
        } else {
          this.props.set_notification_variable(true, 'success', 'Draft saved');
          this.props.on_saved_draft();
        }
      })
      .catch((e) => {
        let err = is_submit ? 'Failed to submit' : 'Failed to save draft';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
        this.setState({ saving: false });
      });
  };

  // ── field renderers ─────────────────────────────────────────────────────────
  // Segmented toggle — one click to pick, no dropdown. Used for Yes / No / N/A
  // and the Overall Status (Red / Yellow / Green). The selected option fills
  // with its colour (opt.color, falling back to brand blue); explicit inline
  // backgrounds override the host CMS's global orange `button {}` rule.
  // Interactive states (hover/focus) and the unanswered highlight live in
  // toggle.css — inline styles can't express pseudo-classes.
  render_toggle = (value, options, onChange) => {
    const answered = options.some((opt) => opt.value === value);
    return (
      <div className={`erc-toggle${answered ? '' : ' erc-toggle--empty'}`}>
        {options.map((opt) => {
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={`erc-toggle-btn${selected ? ' erc-toggle-btn--selected' : ''}`}
              style={selected ? { background: opt.color || '#2563eb' } : undefined}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    );
  };

  render_text_input = (field, type = 'text') => {
    const val = this.state.reply[field.key] ?? '';
    return (
      <CustomInput
        show_label={false}
        type={type}
        name={field.key}
        value={val}
        style={{ width: '220px' }}
        input_style={{ width: '100%' }}
        onChange={(e) => this.set_value(field.key, e.target.value)}
      />
    );
  };

  render_manual_field = (field) => {
    switch (field.shape) {
      case 'yes_no':
        return this.render_toggle(this.state.reply[field.key] || '', YES_NO, (v) =>
          this.set_value(field.key, v)
        );
      case 'yes_no_na':
        return this.render_toggle(this.state.reply[field.key] || '', YES_NO_NA, (v) =>
          this.set_value(field.key, v)
        );
      case 'status':
        return this.render_toggle(this.state.reply[field.key] || '', STATUS_OPTS, (v) =>
          this.set_value(field.key, v)
        );
      case 'count':
        return this.render_text_input(field, 'number');
      case 'date':
        return this.render_text_input(field, 'date');
      case 'text':
        return (
          <div className="w-full">
            {field.key === 'notes_in_hridaya' ? this.render_attachments(false) : null}
            <div className="erc-notes-quill w-full">
              <ReactQuill
                theme="snow"
                value={this.state.reply[field.key] || ''}
                onChange={(html) => this.set_value(field.key, html)}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Add notes…"
              />
            </div>
          </div>
        );
      case 'yes_no_teacher': {
        const obj = this.state.reply[field.key] || {};
        const rows = this.teacher_rows(field.key);
        return (
          <div className="flex flex-col gap-2 items-start">
            {this.render_toggle(obj.value || '', YES_NO, (v) =>
              this.set_obj_value(field.key, 'value', v)
            )}
            {rows.map((t, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className="flex items-center gap-2" key={idx}>
                <div style={{ width: '240px' }}>
                  <SearchableSelect
                    value={t.uuid || ''}
                    options={this.state.teacher_options}
                    placeholder="Search teacher..."
                    clearable
                    onChange={(val) => this.set_teacher(field.key, idx, val)}
                  />
                </div>
                {idx === rows.length - 1 ? (
                  <button
                    type="button"
                    aria-label="Add another teacher"
                    onClick={() => this.add_teacher(field.key)}
                    style={ICON_BTN_STYLE}
                  >
                    <FaPlus size={11} color="#2563eb" />
                  </button>
                ) : null}
                {rows.length > 1 ? (
                  <button
                    type="button"
                    aria-label="Remove teacher"
                    onClick={() => this.remove_teacher(field.key, idx)}
                    style={ICON_BTN_STYLE}
                  >
                    <FaTimes size={11} color="#6b7280" />
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        );
      }
      case 'group_classes_found': {
        const obj = this.state.reply[field.key] || {};
        const rows = this.gc_rows(field.key);
        return (
          <div className="flex flex-col gap-2 items-start">
            {this.render_toggle(obj.value || '', YES_NO_NA, (v) =>
              this.set_obj_value(field.key, 'value', v)
            )}
            {rows.map((cls, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className="flex flex-col gap-1" key={idx}>
                <div className="flex items-center gap-2">
                  <div style={{ width: '240px' }}>
                    <SearchableSelect
                      value={cls.value || ''}
                      options={this.state.group_class_options}
                      placeholder="Search group class..."
                      clearable
                      onChange={(val) => this.set_gc(field.key, idx, val)}
                    />
                  </div>
                  {idx === rows.length - 1 ? (
                    <button
                      type="button"
                      aria-label="Add another group class"
                      onClick={() => this.add_gc(field.key)}
                      style={ICON_BTN_STYLE}
                    >
                      <FaPlus size={11} color="#2563eb" />
                    </button>
                  ) : null}
                  {rows.length > 1 ? (
                    <button
                      type="button"
                      aria-label="Remove group class"
                      onClick={() => this.remove_gc(field.key, idx)}
                      style={ICON_BTN_STYLE}
                    >
                      <FaTimes size={11} color="#6b7280" />
                    </button>
                  ) : null}
                </div>
                {cls.value ? this.render_gc_detail(cls) : null}
              </div>
            ))}
          </div>
        );
      }
      case 'teacher_relationships':
        return (
          <div className="flex flex-col gap-2">
            {this.rel_list(field.key).map((rel, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className="flex gap-2 items-center" key={idx}>
                <div style={{ width: '220px' }}>
                  <SearchableSelect
                    value={rel.uuid || ''}
                    options={this.main_teacher_options()}
                    placeholder="Search teacher..."
                    clearable
                    onChange={(val) => this.set_rel_teacher(field.key, idx, val)}
                  />
                </div>
                {this.render_toggle(rel.strength || '', STRENGTH_OPTS, (v) =>
                  this.update_rel(field.key, idx, 'strength', v)
                )}
                <button
                  type="button"
                  aria-label="Remove teacher"
                  onClick={() => this.remove_rel(field.key, idx)}
                  style={ICON_BTN_STYLE}
                >
                  <FaTrash size={11} color="#ef4444" />
                </button>
              </div>
            ))}
            <div>
              <Button
                type="secondary"
                custom_style={{ padding: '2px 10px', fontSize: '12px' }}
                onClick={() => this.add_rel(field.key)}
              >
                + Add teacher
              </Button>
            </div>
          </div>
        );
      default:
        return this.render_text_input(field);
    }
  };

  // Repeats Setup: count + one line per slot, teacher name links to profile.
  render_repeats = (raw) => {
    const list = Array.isArray(raw?.repeats) ? raw.repeats : [];
    const count = raw?.count ?? list.length;
    return (
      <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
        <div className="font-medium">{count}</div>
        {list.map((r, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={idx} className="py-0.5">
            {r.day_time} -{' '}
            {r.teacher_uuid ? (
              <Link
                to={`/teacherprofile?id=${encodeURIComponent(r.teacher_uuid)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                {r.teacher_name || r.teacher_uuid}
              </Link>
            ) : (
              r.teacher_name || '—'
            )}{' '}
            - {r.times}x
          </div>
        ))}
      </div>
    );
  };

  // "Name — Teacher (clickable) — Day Time" line for a selected group class.
  render_gc_detail = (cls) => (
    <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
      <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
        {cls.name}
      </span>
      {' — '}
      {cls.teacher_uuid ? (
        <Link
          to={`/teacherprofile?id=${encodeURIComponent(cls.teacher_uuid)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          {cls.teacher_name || cls.teacher_uuid}
        </Link>
      ) : (
        cls.teacher_name || '—'
      )}
      {cls.day_of_week ? ` — ${cls.day_of_week} ${cls.time || ''}` : ''}
    </div>
  );

  // Read-only view of Group Classes Found: the Yes/No/NA value + each class line.
  render_gc_readonly = (raw) => {
    const list = Array.isArray(raw?.classes) ? raw.classes : [];
    return (
      <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
        <div>{raw?.value || '—'}</div>
        {list.map((cls, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={idx} className="py-0.5">
            {this.render_gc_detail(cls)}
          </div>
        ))}
      </div>
    );
  };

  render_readonly_field = (field) => {
    const isAuto = field.read_only;
    const raw = isAuto ? this.state.auto_data[field.key] : this.state.reply[field.key];
    // Auto (blue) values are server-computed and stubbed this round.
    if (isAuto && (raw === undefined || raw === null || raw === '')) {
      return (
        <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
          Auto-populated (coming soon)
        </span>
      );
    }
    if (field.shape === 'repeats') {
      return this.render_repeats(raw);
    }
    if (field.shape === 'group_classes_found' && raw && Array.isArray(raw.classes)) {
      return this.render_gc_readonly(raw);
    }
    if (field.shape === 'text') {
      return (
        <div>
          {field.key === 'notes_in_hridaya' ? this.render_attachments(true) : null}
          {/* eslint-disable-next-line react/no-danger */}
          <div
            className="text-sm erc-notes-readonly"
            style={{ color: 'var(--text-primary)' }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(raw || '') }}
          />
        </div>
      );
    }
    return (
      <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
        {format_field_value(field.shape, raw)}
        {field.key === 'teacher_created_3m_plan' &&
        raw === 'Yes' &&
        this.state.meta.student_uuid ? (
          <Link
            to={`/personalised-plan?student_uuid=${encodeURIComponent(this.state.meta.student_uuid)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2 text-blue-600 hover:underline"
          >
            View 3 Month Plan
          </Link>
        ) : null}
      </span>
    );
  };

  // Membership Cancellation Date is only relevant once the student has actually
  // cancelled — hide it when auto_data has no value for it.
  is_hidden_field = (key) => {
    if (key === 'membership_cancellation_date') {
      const v = this.state.auto_data[key];
      return v === undefined || v === null || v === '';
    }
    return false;
  };

  // Collapse the configured pairs into combined rows; everything else stays a
  // single-field row. Order follows the server field list.
  build_rows = () => {
    const byKey = {};
    this.state.fields.forEach((f) => {
      byKey[f.key] = f;
    });
    const primaryOf = {};
    const merged = {};
    COMBINE_GROUPS.forEach((keys) => {
      if (keys.every((k) => byKey[k])) {
        primaryOf[keys[0]] = keys.map((k) => byKey[k]);
        keys.slice(1).forEach((k) => {
          merged[k] = true;
        });
      }
    });
    const rows = [];
    this.state.fields.forEach((f) => {
      if (merged[f.key]) return;
      if (this.is_hidden_field(f.key)) return;
      rows.push({ key: f.key, parts: primaryOf[f.key] || [f] });
    });
    return rows;
  };

  // Renders one row holding one or more fields (combined pairs stack each
  // label/value on its own aligned line).
  render_field_row = (row) => {
    const readOnly = this.props.read_only || this.state.meta.status === 'submitted';
    return (
      <div
        className="flex items-start gap-4 py-2 border-b"
        style={{ borderColor: 'var(--border-default)' }}
        key={row.key}
      >
        <div className="w-1/2 text-sm" style={{ color: 'var(--text-primary)' }}>
          {row.parts.map((field) => (
            <div key={field.key} className="py-0.5">
              {field.label}
              {field.read_only ? (
                <span className="ml-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                  (auto)
                </span>
              ) : null}
            </div>
          ))}
        </div>
        <div className="w-1/2">
          {row.parts.map((field) => {
            const showValue = field.read_only || readOnly;
            return (
              <div key={field.key} className="py-0.5">
                {showValue ? this.render_readonly_field(field) : this.render_manual_field(field)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Rounded pill: muted tag label + emphasised value.
  render_pill = (label, value) => (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs"
      style={{
        background: 'var(--surface-sunken)',
        border: '1px solid var(--border-default)'
      }}
    >
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
        {value}
      </span>
    </span>
  );

  // Attachments section shown below the notes/fields.
  render_attachments = (read_only) => {
    const list = this.attachments();
    const { uploading } = this.state;
    return (
      <div className="mb-2">
        <div className="text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
          Attachments
        </div>
        {list.length === 0 ? (
          <div className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
            No attachments.
          </div>
        ) : (
          <div className="flex flex-col gap-1 mb-2">
            {list.map((att, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={idx} className="flex items-center gap-2 text-sm">
                <FaPaperclip size={11} style={{ color: 'var(--text-muted)' }} />
                <a
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {att.name || att.key}
                </a>
                {!read_only ? (
                  <button
                    type="button"
                    aria-label="Remove attachment"
                    onClick={() => this.remove_attachment(idx)}
                    style={{ ...ICON_BTN_STYLE, height: 22, width: 22 }}
                  >
                    <FaTimes size={10} color="#6b7280" />
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        )}
        {!read_only ? (
          <>
            <input
              ref={this.file_input_ref}
              type="file"
              style={{ display: 'none' }}
              onChange={this.handle_attachment_change}
            />
            <button
              type="button"
              onClick={() => this.file_input_ref.current && this.file_input_ref.current.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium border"
              style={{
                borderColor: 'var(--input-border)',
                background: 'var(--input-bg)',
                color: 'var(--text-primary)',
                cursor: uploading ? 'wait' : 'pointer'
              }}
            >
              <FaPaperclip size={11} />
              {uploading ? 'Uploading…' : 'Add attachment'}
            </button>
          </>
        ) : null}
      </div>
    );
  };

  render() {
    const { loading, saving, meta } = this.state;
    const read_only = this.props.read_only || meta.status === 'submitted';

    if (loading) {
      return <CustomLoading />;
    }

    return (
      <div>
        <div className="text-xl font-semibold mb-1">
          {meta.student_uuid ? (
            <Link
              to={`/studentprofile?id=${encodeURIComponent(meta.student_uuid)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {meta.student_name}
            </Link>
          ) : (
            <span style={{ color: 'var(--text-primary)' }}>{meta.student_name}</span>
          )}
        </div>

        {/* Check label + read-only metadata pills. */}
        <div className="flex items-center flex-wrap gap-2 mb-4 text-sm">
          <span
            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
            style={{
              background: 'var(--state-info-bg, #e0e7ff)',
              color: 'var(--state-info-text, #3730a3)'
            }}
          >
            {meta.check_label}
          </span>
          {meta.checkin_date ? this.render_pill('Checkin Date', meta.checkin_date) : null}
          {meta.pause_days_added > 0
            ? this.render_pill('Pause Days', `+${meta.pause_days_added}`)
            : null}
          {meta.submitted_by_name
            ? this.render_pill('Checklist filled by', meta.submitted_by_name)
            : null}
        </div>

        {!read_only && this.count_unanswered() > 0 ? (
          <button
            type="button"
            className="erc-unanswered-banner"
            onClick={this.scroll_to_first_unanswered}
          >
            <FaExclamationTriangle size={14} />
            {this.count_unanswered()} question
            {this.count_unanswered() === 1 ? '' : 's'} still need an answer — click to jump to the
            first
          </button>
        ) : null}

        <div className="max-h-[60vh] overflow-y-auto pr-2" ref={this.scroll_ref}>
          {this.build_rows().map(this.render_field_row)}
        </div>

        {read_only ? (
          <div className="mt-6 text-sm" style={{ color: 'var(--text-muted)' }}>
            This checklist has been submitted and is read-only.
          </div>
        ) : (
          <div className="mt-6 flex justify-end gap-3">
            <Button type="secondary" disabled={saving} onClick={this.save_draft}>
              Save to draft
            </Button>
            <Button disabled={saving} onClick={this.submit}>
              {saving ? 'Saving...' : 'Submit'}
            </Button>
          </div>
        )}
      </div>
    );
  }
}

ChecklistForm.propTypes = {
  uuid: PropTypes.string.isRequired,
  read_only: PropTypes.bool,
  close: PropTypes.func.isRequired,
  on_submitted: PropTypes.func.isRequired,
  on_saved_draft: PropTypes.func.isRequired,
  set_notification_variable: PropTypes.func.isRequired
};

ChecklistForm.defaultProps = {
  read_only: false
};

const mapStateToProps = () => ({});
const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(ChecklistForm);
